import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faEye,
  faFile,
  faFileLines,
  faSpinner,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import Drawer from "@/components/ui/drawer";
import { Button } from "@/components/ui/button/Button";
import { CC_INSET, CC_SECTION_LABEL } from "@/components/case-checking/caseCheckingChrome";
import {
  downloadCaseDocumentFile,
  getCaseDocuments,
  viewCaseDocumentFile,
  type CaseDocument,
  type ProblemStatementRuleSnapshot,
} from "@/services/api/case-checking";
import { caseDocumentCanViewInline } from "@/components/case-checking/caseDocumentFile";

type LinkedDoc = {
  ref: string;
  document?: CaseDocument;
  ruleLabels: string[];
};

function collectLinkedDocs(rules: ProblemStatementRuleSnapshot[], documents: CaseDocument[]): LinkedDoc[] {
  const byRef = new Map<string, LinkedDoc>();
  const docsById = new Map(documents.map((d) => [d.id, d]));

  for (const rule of rules) {
    for (const ref of rule.document_refs ?? []) {
      const key = ref.trim();
      if (!key) continue;
      const existing = byRef.get(key);
      const doc = docsById.get(key);
      if (existing) {
        if (!existing.ruleLabels.includes(rule.label || rule.rule_id)) {
          existing.ruleLabels.push(rule.label || rule.rule_id);
        }
        if (!existing.document && doc) existing.document = doc;
      } else {
        byRef.set(key, {
          ref: key,
          document: doc,
          ruleLabels: [rule.label || rule.rule_id],
        });
      }
    }
  }

  return Array.from(byRef.values()).sort((a, b) => {
    const an = a.document?.filename ?? a.ref;
    const bn = b.document?.filename ?? b.ref;
    return an.localeCompare(bn);
  });
}

function formatClassification(classification: string | null): string {
  if (!classification) return "Unknown";
  return classification.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const DOC_ACTION_BTN =
  "rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-white/[0.06] hover:text-slate-700 dark:hover:text-slate-200 disabled:pointer-events-none disabled:opacity-40";

function DocumentSummaryDrawer({
  document,
  onClose,
}: {
  document: CaseDocument;
  onClose: () => void;
}) {
  return (
    <Drawer key={document.id} isOpen onClose={onClose} title={document.filename} width="max-w-lg">
      <div className="space-y-5 px-6 pb-6 pt-1">
        <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium text-slate-400 ring-1 ring-slate-200 dark:ring-white/10">
          {formatClassification(document.classification)}
        </span>
        {document.summary ? (
          <section>
            <p className={CC_SECTION_LABEL}>Summary</p>
            <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{document.summary}</p>
          </section>
        ) : (
          <p className="text-sm text-slate-500">No AI summary for this document yet.</p>
        )}
        {document.classification_reasoning ? (
          <section>
            <p className={CC_SECTION_LABEL}>Classification notes</p>
            <p className="text-sm leading-relaxed text-slate-400">{document.classification_reasoning}</p>
          </section>
        ) : null}
      </div>
    </Drawer>
  );
}

export function StatementSupportingDocs({
  caseId,
  rules,
  documents: documentsProp,
  documentsLoading: documentsLoadingProp,
  compact = false,
  emptyMessage,
}: {
  caseId: string;
  rules: ProblemStatementRuleSnapshot[];
  documents?: CaseDocument[];
  documentsLoading?: boolean;
  /** Tighter layout for per-rule embeds (e.g. guided Rules step). */
  compact?: boolean;
  emptyMessage?: string;
}) {
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [loading, setLoading] = useState(documentsProp === undefined);
  const [summaryDoc, setSummaryDoc] = useState<CaseDocument | null>(null);
  const [fileAction, setFileAction] = useState<{ id: string; kind: "view" | "download" } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    if (documentsProp !== undefined) return;
    let cancelled = false;
    setLoading(true);
    getCaseDocuments(caseId)
      .then((docs) => {
        if (!cancelled) setDocuments(docs ?? []);
      })
      .catch(() => {
        if (!cancelled) setDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, documentsProp]);

  const resolvedDocuments = documentsProp ?? documents;
  const resolvedLoading = documentsLoadingProp ?? loading;

  const linked = useMemo(() => collectLinkedDocs(rules, resolvedDocuments), [rules, resolvedDocuments]);
  const hasRefs = rules.some((r) => (r.document_refs?.length ?? 0) > 0);

  const runFileAction = async (doc: CaseDocument, kind: "view" | "download") => {
    setFileError(null);
    setFileAction({ id: doc.id, kind });
    try {
      if (kind === "view") {
        await viewCaseDocumentFile(caseId, doc);
      } else {
        await downloadCaseDocumentFile(caseId, doc);
      }
    } catch (err) {
      console.error(`Failed to ${kind} document:`, err);
      setFileError(`Could not ${kind} ${doc.filename}.`);
    } finally {
      setFileAction(null);
    }
  };

  if (!hasRefs && !resolvedLoading) return null;

  const emptyCopy =
    emptyMessage ??
    (compact ? "No linked documents for this rule." : "No linked documents found for this statement.");

  return (
    <div
      className={clsx(compact ? "space-y-1.5" : "space-y-1.5 border-t border-slate-200 dark:border-white/5 pt-4")}
      data-testid="statement-supporting-docs"
    >
      <p className="text-xs text-slate-500">
        Supporting documents
        {!resolvedLoading && linked.length > 0 ? ` · ${linked.length} file${linked.length === 1 ? "" : "s"}` : ""}
      </p>

      {fileError ? (
        <p className="text-xs text-red-700 dark:text-red-300">{fileError}</p>
      ) : null}

      {resolvedLoading ? (
        <p className="flex items-center gap-2 text-xs text-slate-500">
          <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
          Loading…
        </p>
      ) : linked.length === 0 ? (
        <p className="text-xs text-slate-500">{emptyCopy}</p>
      ) : (
        <ul className="space-y-1.5">
          {linked.map((item) => {
            const label = item.document?.filename ?? `Document ${item.ref.slice(-6)}`;
            const doc = item.document;
            const canOpen = Boolean(doc);
            const canView = doc ? caseDocumentCanViewInline(doc.file_type, doc.filename) : false;
            const busy = doc && fileAction?.id === doc.id;
            return (
              <li
                key={item.ref}
                className={clsx("flex items-center gap-2", CC_INSET, "p-0 px-3 py-2")}
              >
                <FontAwesomeIcon icon={faFile} className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                <span className="min-w-0 flex-1 truncate text-sm text-slate-700 dark:text-slate-200" title={label}>
                  {label}
                </span>
                {doc ? (
                  <span className="hidden shrink-0 text-[11px] text-slate-500 sm:inline">
                    {formatClassification(doc.classification)}
                  </span>
                ) : (
                  <span className="shrink-0 text-[11px] text-amber-400/90">Missing</span>
                )}
                <div className="flex shrink-0 items-center gap-0.5">
                  <Button
                    plain size="unstyled"
                    type="button"
                    className={clsx("p-1.5", DOC_ACTION_BTN)}
                    disabled={!canOpen}
                    onClick={() => doc && setSummaryDoc(doc)}
                    title="Summary"
                    aria-label={`Summary for ${label}`}
                    data-testid={`doc-summary-${item.ref}`}
                  >
                    <FontAwesomeIcon icon={faFileLines} className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    plain size="unstyled"
                    type="button"
                    className={clsx("p-1.5", DOC_ACTION_BTN)}
                    disabled={!canOpen || Boolean(busy)}
                    onClick={() => doc && void runFileAction(doc, "download")}
                    title="Download"
                    aria-label={`Download ${label}`}
                    data-testid={`doc-download-${item.ref}`}
                  >
                    <FontAwesomeIcon
                      icon={busy && fileAction?.kind === "download" ? faSpinner : faDownload}
                      className={clsx("h-3.5 w-3.5", busy && fileAction?.kind === "download" && "animate-spin")}
                    />
                  </Button>
                  {canView ? (
                    <Button
                      plain size="unstyled"
                      type="button"
                      className={clsx("p-1.5", DOC_ACTION_BTN)}
                      disabled={Boolean(busy)}
                      onClick={() => doc && void runFileAction(doc, "view")}
                      title="View"
                      aria-label={`View ${label}`}
                      data-testid={`doc-view-${item.ref}`}
                    >
                      <FontAwesomeIcon
                        icon={busy && fileAction?.kind === "view" ? faSpinner : faEye}
                        className={clsx("h-3.5 w-3.5", busy && fileAction?.kind === "view" && "animate-spin")}
                      />
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {summaryDoc ? <DocumentSummaryDrawer document={summaryDoc} onClose={() => setSummaryDoc(null)} /> : null}
    </div>
  );
}
